import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  TrendingUp,
  AlertTriangle,
  Target,
  Award,
  BookOpen,
  Lightbulb,
  User,
  Briefcase,
  FileText,
  Sparkles
} from 'lucide-react';
import TopMenu from '../TopMenu';
import './ATSResultsPage.css';

const ATSResultsPage = ({ theme, toggleTheme, data, onBack }) => {
  const navigate = useNavigate();

  const atsScore = Number(data?.atsScore ?? 0);
  const matchPercentage = Number(data?.matchPercentage ?? 0);
  const keywordDensity = Number(data?.keywordDensity ?? 0);
  const matchedSkills = Array.isArray(data?.matchedSkills) ? data.matchedSkills : [];
  const missingSkills = Array.isArray(data?.missingSkills) ? data.missingSkills : [];
  const strengths = Array.isArray(data?.strengths) ? data.strengths : [];
  const weaknesses = Array.isArray(data?.weaknesses) ? data.weaknesses : [];
  const recommendations = Array.isArray(data?.recommendations) ? data.recommendations : [];

  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    return 'Needs Improvement';
  };

  const scoreColor = getScoreColor(atsScore);

  const handleCreateResume = () => {
    navigate('/resume-builder');
  };

  const infoCards = [
    {
      icon: User,
      label: 'Candidate',
      value: data?.candidateName || 'Your Profile'
    },
    {
      icon: Briefcase,
      label: 'Position',
      value: data?.jobTitle || 'Software Engineer'
    },
    {
      icon: Award,
      label: 'ATS Verdict',
      value: getScoreLabel(atsScore),
      valueStyle: { color: scoreColor }
    },
    {
      icon: TrendingUp,
      label: 'Experience Level',
      value: data?.experienceLevel || 'Junior-Mid Level'
    },
    {
      icon: Target,
      label: 'Best Fit For',
      value: data?.bestFitFor || 'Full Stack .NET Developer',
      fullWidth: true
    }
  ];

  const statCards = [
    {
      icon: Target,
      value: matchedSkills.length,
      label: 'Matched Skills'
    },
    {
      icon: AlertTriangle,
      value: missingSkills.length,
      label: 'Missing Skills'
    },
    {
      icon: TrendingUp,
      value: `${Math.round(keywordDensity)}%`,
      label: 'Keyword Density'
    }
  ];

  const sections = [
    {
      className: 'matched-skills',
      icon: CheckCircle,
      color: '#22c55e',
      title: 'Matched Skills',
      content: (
        <div className="skills-list">
          {matchedSkills.length ? matchedSkills.map((skill, idx) => (
            <motion.div
              key={`${skill}-${idx}`}
              className="skill-tag matched"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1 + idx * 0.03 }}
            >
              {skill}
            </motion.div>
          )) : <p className="empty-state">No matched skills detected yet.</p>}
        </div>
      )
    },
    {
      className: 'missing-skills',
      icon: XCircle,
      color: '#ef4444',
      title: 'Missing Skills',
      content: (
        <div className="skills-list">
          {missingSkills.length ? missingSkills.map((skill, idx) => (
            <motion.div
              key={`${skill}-${idx}`}
              className="skill-tag missing"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1 + idx * 0.03 }}
            >
              {skill}
            </motion.div>
          )) : <p className="empty-state">No missing skills found.</p>}
        </div>
      )
    },
    {
      className: 'strengths',
      icon: Award,
      color: '#22c55e',
      title: 'Your Strengths',
      content: (
        <ul className="list-items">
          {strengths.length ? strengths.map((strength, idx) => (
            <motion.li
              key={`${strength}-${idx}`}
              initial={{ x: -16, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.15 + idx * 0.05 }}
            >
              {strength}
            </motion.li>
          )) : <li className="empty-list-item">No strengths captured yet.</li>}
        </ul>
      )
    },
    {
      className: 'weaknesses',
      icon: AlertTriangle,
      color: '#f59e0b',
      title: 'Areas to Improve',
      content: (
        <ul className="list-items">
          {weaknesses.length ? weaknesses.map((weakness, idx) => (
            <motion.li
              key={`${weakness}-${idx}`}
              initial={{ x: -16, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.15 + idx * 0.05 }}
            >
              {weakness}
            </motion.li>
          )) : <li className="empty-list-item">No improvement areas identified.</li>}
        </ul>
      )
    },
    {
      className: 'recommendations',
      icon: Lightbulb,
      color: '#3b82f6',
      title: 'Recommendations',
      content: (
        <ul className="list-items">
          {recommendations.length ? recommendations.map((rec, idx) => (
            <motion.li
              key={`${rec}-${idx}`}
              initial={{ x: -16, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.15 + idx * 0.05 }}
            >
              {rec}
            </motion.li>
          )) : <li className="empty-list-item">No recommendations available yet.</li>}
        </ul>
      )
    }
  ];

  return (
    <div className={`ats-results-page ${theme}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />

      <div className="results-shell">
        <div className="results-background" aria-hidden="true" />

        <div className="results-container">
          <div className="header-buttons">
            <motion.button
              className="back-button"
              onClick={onBack}
              whileHover={{ scale: 1.03, x: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <ArrowLeft size={18} />
              Analyze Another Resume
            </motion.button>

            <motion.button
              className="create-resume-button-header"
              onClick={handleCreateResume}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              <FileText size={18} />
              <span>Create ATS Resume</span>
              <Sparkles size={15} className="sparkle-icon" />
            </motion.button>
          </div>

          <motion.section
            className="hero-card"
            initial={{ y: 26, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 110, damping: 16 }}
          >
            <div className="hero-copy">
              <span className="eyebrow">ATS ANALYSIS COMPLETE</span>
              <h1 className="results-title">A sharper read on your resume, instantly.</h1>
              <p className="results-subtitle">
                We compared your profile against the target role and surfaced the strongest signals,
                the gaps to close, and the next best actions.
              </p>

              <div className="hero-metadata">
                <div className="meta-pill">
                  <User size={14} />
                  <span>{data?.candidateName || 'Your Profile'}</span>
                </div>
                <div className="meta-pill">
                  <Briefcase size={14} />
                  <span>{data?.jobTitle || 'Software Engineer'}</span>
                </div>
                <div className="meta-pill accent">
                  <TrendingUp size={14} />
                  <span>{Math.round(matchPercentage)}% Match</span>
                </div>
              </div>

              <div className="signal-strip">
                <div className="signal-item">
                  <span className="signal-label">Verdict</span>
                  <strong style={{ color: scoreColor }}>{getScoreLabel(atsScore)}</strong>
                </div>
                <div className="signal-item">
                  <span className="signal-label">Keyword Density</span>
                  <strong>{Math.round(keywordDensity)}%</strong>
                </div>
                <div className="signal-item">
                  <span className="signal-label">Matched Skills</span>
                  <strong>{matchedSkills.length}</strong>
                </div>
              </div>
            </div>

            <div className="score-panel">
              <div className="score-card" style={{ '--score-color': scoreColor }}>
                <div className="score-card-top">
                  <span className="score-card-label">ATS Score</span>
                  <span className="score-card-badge" style={{ background: scoreColor }}>
                    {getScoreLabel(atsScore)}
                  </span>
                </div>

                <motion.div
                  className="score-number-large"
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.14, type: 'spring', stiffness: 180 }}
                >
                  {Math.round(atsScore)}
                </motion.div>

                <div className="score-caption">
                  Stronger than a standard ATS summary. This view highlights what matters most.
                </div>

                <div className="score-meter">
                  <div
                    className="score-meter-fill"
                    style={{ width: `${Math.min(Math.max(atsScore, 0), 100)}%`, background: scoreColor }}
                  />
                </div>

                <div className="score-support">
                  <div className="match-percentage">
                    <TrendingUp size={16} />
                    <span>{Math.round(matchPercentage)}% Match</span>
                  </div>
                  <div className="score-mini-note">No ring, no clutter, just signal.</div>
                </div>
              </div>
            </div>
          </motion.section>

          <section className="quick-stats">
            {statCards.map((item) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.label}
                  className="stat-card"
                  initial={{ y: 18, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 }}
                >
                  <div className="stat-icon">
                    <Icon size={22} />
                  </div>
                  <div>
                    <div className="stat-value">{item.value}</div>
                    <div className="stat-label">{item.label}</div>
                  </div>
                </motion.div>
              );
            })}
          </section>

          <motion.section
            className="info-panel"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            <div className="section-heading">
              <div>
                <span className="section-kicker">Profile Snapshot</span>
                <h2>Key analysis details</h2>
              </div>
            </div>

            <div className="info-grid">
              {infoCards.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.label}
                    className={`info-card ${card.fullWidth ? 'full-width' : ''}`}
                  >
                    <div className="info-icon">
                      <Icon size={18} />
                    </div>
                    <div className="info-content">
                      <div className="info-label">{card.label}</div>
                      <div className="info-value" style={card.valueStyle}>{card.value}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.section>

          <div className="results-grid">
            {sections.map((section, idx) => {
              const Icon = section.icon;
              return (
                <motion.article
                  key={section.title}
                  className={`result-card ${section.className}`}
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 + idx * 0.06 }}
                >
                  <div className="card-header">
                    <div className="card-icon" style={{ color: section.color }}>
                      <Icon size={22} />
                    </div>
                    <h2>{section.title}</h2>
                  </div>
                  {section.content}
                </motion.article>
              );
            })}
          </div>

          <motion.article
            className="result-card overall-feedback"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.45 }}
          >
            <div className="card-header">
              <div className="card-icon" style={{ color: '#8b5cf6' }}>
                <BookOpen size={22} />
              </div>
              <h2>Overall Feedback</h2>
            </div>
            <p className="feedback-text">{data?.overallFeedback || 'No overall feedback available yet.'}</p>
          </motion.article>

          <div className="match-badges">
            <div className="badge">
              <span className="badge-label">Experience Match:</span>
              <span className={`badge-value ${data?.experienceMatch || ''}`}>
                {data?.experienceMatch || 'unknown'}
              </span>
            </div>
            <div className="badge">
              <span className="badge-label">Education Match:</span>
              <span className={`badge-value ${data?.educationMatch || ''}`}>
                {data?.educationMatch || 'unknown'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ATSResultsPage;
